// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { func, bool, string, arrayOf, shape } from 'prop-types';
import {
  Button,
  Typography,
  withStyles,
} from '@material-ui/core';

import {
  AlertMessages,
  FileDropzone,
  FormTextField,
} from '../../../../../components';
import styles from './styles';
import { getI18n, getI18nFormattedMessage } from '../../../../../utils/intl';

import { compose } from 'recompose';
import { isArray } from 'lodash';
import { getFileExtension, getFirstValidationError, getValidationErrorFields } from '../../../../../utils';
import { ALERT_ERROR } from '../../../../../constants';

const AddCertificate = (props) => {
  const {
    classes,
    addAppCertificate,
    appUuid,
    addCertStatus,
    validationErrors,
    onClose,
    workFlowMode,
  } = props;

  const intl = getI18n(useIntl());
  const [name, setName] = useState('');
  const [isFileValidation, setIsFileValidation] = useState(false);
  const [fileValidationMessage, setFileValidationMessage] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [nameError, setNameError] = useState(false);
  const FILE_HELPER_TEXT = intl.getI18nMessage('label.upload.file.help.text', { type : 'certificate', size: '50KB' });
  const MAX_FILE_SIZE = 50*1024;

  const [sslFile, setSslFile] = useState({
    files: [],
    maxFiles: 1,
    error: false,
    helperText: FILE_HELPER_TEXT,
    certificate: '',
    certificateFileName: '',
  });
  const handleFiles = (files) => {
    notifyMessagesInDialog('', '');
    if (isArray(files)) {
      files.forEach((file) => {
        fileLoad(file);
      });
    } else {
      fileLoad(files);
    }
    localStorage.setItem('isUnSavedChanges', true);
  };

  const onRemoveFile = (file) => {
    const remainFiles = sslFile.files.filter((obj) => obj.name !== file.name);
    setSslFile({
      ...sslFile,
      files: remainFiles,
      certificate: '',
      certificateFileName: '',
    });
    localStorage.setItem('isUnSavedChanges', true);
    if(file.name === name) {
      setName('');
      setNameError(false);
      setIsFileValidation(false);
    }
    notifyMessagesInDialog('', '');
  };
  const fileLoad = (file) => {
    const reader = new FileReader();
    reader.onabort = () => {
      sslFile.error = true;
      setSslFile({ ...sslFile });
    };
    reader.onerror = () => {
      sslFile.error = true;
      setSslFile({ ...sslFile });
    };
    reader.onload = (e) => {
      if (hasValidateFile(file)) {
        sslFile.error = false;
        const fileContent = reader.result;
        const fileExtenstion = getFileExtension(file.name);
        const fileData = file;
        fileData.ext = fileExtenstion;
        fileData.blob = new Blob(
          [fileContent],
        );
        sslFile.certificate = e.target.result;
        sslFile.certificateFileName = file.name;
        if(!name) {
          setName(file.name);
        }
        sslFile.files.push(fileData);
        setSslFile({ ...sslFile });
      } else {
        setSslFile({ ...sslFile });
      }
    };
    reader.readAsDataURL(file);
  };
  const hasValidateFile = (file) => {

    if (file.size > MAX_FILE_SIZE) {
      sslFile.error = true;
      setIsFileValidation(true);
      setFileValidationMessage(getI18nFormattedMessage('label.error.upload.file.size'));
      return false;
    }
    if (sslFile.files.length === 1) {
      setIsFileValidation(true);
      setFileValidationMessage(getI18nFormattedMessage('label.error.app.cert.file.count'));
      return false;
    }
    setIsFileValidation(false);
    return true;
  };
  const saveCertificate = () => {
    const certContentArray = sslFile.certificate.split(",");
    const base64 = certContentArray.length > 1 ? certContentArray[1] : '';
    addAppCertificate(appUuid,
      {
        name,
        certificateFileName: sslFile.certificateFileName,
        certificateContent: base64,
      });
  }
  const notifyMessagesInDialog = (message, variant) => {
    setNotificationMessage(message);
    setNotificationStatus(variant);
  }
  useEffect(() => {
    if(addCertStatus === 'ERROR' && validationErrors) {
      if(getValidationErrorFields(validationErrors).includes("Name")) {
        setNameError(true);
      }
      notifyMessagesInDialog(
        getFirstValidationError(validationErrors, getI18nFormattedMessage(`label.application.details.certificates.add.${workFlowMode ? 'request.' : ''}failed`)), ALERT_ERROR);
    } else {
      notifyMessagesInDialog('', '');
      setNameError(false);
    }
  },[addCertStatus]);
  const onNameChange = (value) => {
    setName(value);
    notifyMessagesInDialog('', '');
    setNameError(false);
  }

  return (
    <div id="cert-add-dialog-container" className={classes.addCertPanel}>
      {notificationMessage &&
        <AlertMessages
          id="add-cert-notification"
          variant={notificationStatus}
          message={notificationMessage}
          containerClass={classes.notificationWidthClass}
          onClose={() => { notifyMessagesInDialog('', ''); }}
        />
      }
      <Fragment>
        <Typography variant="h3" classes={{ root: classes.sectionTitle }} gutterBottom>
          {getI18nFormattedMessage('label.application.details.certificates.add.dialog.title')}
        </Typography>
        <FormTextField
          id="cert-name"
          name={intl.getI18nMessage('label.application.details.certificates.add.dialog.name.field')}
          value={name}
          helperText={intl.getI18nMessage('label.application.details.certificates.add.dialog.name.field.helpertext')}
          handleChange={onNameChange}
          maxLength="255"
          autoFocus={true}
          error={nameError}
        />
        <FileDropzone
          handleFiles={handleFiles}
          selectedFiles={sslFile}
          labelClass={classes.fileLabel}
          defaultUploaderText={intl.getI18nMessage('label.application.details.certificates.add.uploader.title')}
          id={'upload-cert-file-drop-zone'}
          onRemoveFile={onRemoveFile}
          isValidation={isFileValidation}
          validationMessage={fileValidationMessage}
        />
        <div className={classes.footer}>
          <div className={classes.assistText}>
            {getI18nFormattedMessage(`label.application.details.certificates.add.dialog.${workFlowMode? 'workflow.' : ''}assist.text`)}
          </div>
          <div className={classes.dialogButtonPanel}>
            <Button
              id="btn-cert-save"
              className={classes.saveButton}
              color="secondary"
              variant="contained"
              onClick={saveCertificate}
              disabled={!sslFile.certificate || !name}
            >
              {getI18nFormattedMessage(`label.application.details.certificates.add.dialog.${workFlowMode ? 'submit' : 'save'}`)}
            </Button>
            <Button
              id="btn-close"
              color="primary"
              variant="outlined"
              onClick={() => onClose()}
              className={classes.closeButton}
            >
              {getI18nFormattedMessage('label.application.details.certificates.add.dialog.cancel')}
            </Button>
          </div>
      </div>
      </Fragment>
    </div>
  );
};

AddCertificate.propTypes = {
  classes: shape({}),
  workFlowMode: bool,
  onClose: func,
  validationErrors: arrayOf(shape({})),
  addCertStatus: string,
  appUuid: string,
  addAppCertificate: func,
};

export default compose(
  withStyles(styles),
)(AddCertificate);
